import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { cn } from '@/shared/helpers/cn';
import styles from './Sidebar.module.scss';

export interface SidebarItem {
  to: string;
  label: string;
  icon?: ReactNode;
  /** Casa apenas a rota exata. Use na raiz de uma seção. */
  end?: boolean;
}

export interface SidebarSection {
  /** Micro-label da seção: ATENDIMENTO, GESTÃO, AJUDA. */
  label?: string;
  items: readonly SidebarItem[];
}

export interface SidebarProps {
  sections: readonly SidebarSection[];
  /** Ação primária em pill, acima da navegação. */
  primaryAction?: ReactNode;
  /** Colapsa para 64px, mostrando apenas os ícones. */
  collapsed?: boolean;
  className?: string;
}

/**
 * Navegação principal do app: 250px, fundo branco contra o `--bg` da página.
 *
 * O item ativo recebe **dois sinais** — cor royal *mais* fundo `--surface-brand`
 * e a barra de 3px — além de `aria-current="page"`. Cor sozinha não sobrevive a
 * daltonismo nem a monitor de consultório, e foi exatamente a dívida registrada
 * no design system sobre a sidebar em produção.
 */
export function Sidebar({ sections, primaryAction, collapsed, className }: SidebarProps) {
  return (
    <nav
      className={cn(styles.sidebar, collapsed && styles.collapsed, className)}
      aria-label="Navegação principal"
    >
      {primaryAction ? <div className={styles.primary}>{primaryAction}</div> : null}

      {sections.map((section, index) => (
        <div key={section.label ?? index} className={styles.section}>
          {section.label && !collapsed ? <p className={styles.sectionLabel}>{section.label}</p> : null}
          <ul className={styles.list}>
            {section.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => cn(styles.link, isActive && styles.active)}
                  title={collapsed ? item.label : undefined}
                >
                  {item.icon ? (
                    <span className={styles.icon} aria-hidden="true">
                      {item.icon}
                    </span>
                  ) : null}
                  <span className={cn(styles.label, collapsed && styles.labelHidden)}>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}
